import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface VocabularyWord {
  id: string;
  word: string;
  reading: string | null;
  romaji: string | null;
  meaning: string | null;
  part_of_speech: string | null;
  example_sentence: string | null;
  source_video_id: string | null;
  start_time: number | null;
  end_time: number | null;
  created_at: string;
}

export function useVocabulary() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [vocabulary, setVocabulary] = useState<VocabularyWord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchVocabulary = async () => {
    if (!user) {
      setVocabulary([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("vocabulary")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setVocabulary(data || []);
    } catch (error) {
      console.error("Error fetching vocabulary:", error);
      toast({
        title: "获取生词本失败",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addWord = async (word: {
    word: string;
    reading?: string;
    romaji?: string;
    meaning?: string;
    part_of_speech?: string;
    example_sentence?: string;
    source_video_id?: string;
    start_time?: number;
    end_time?: number;
  }) => {
    if (!user) return { error: new Error("未登录") };

    try {
      // Check if word already exists
      const { data: existing } = await supabase
        .from("vocabulary")
        .select("id")
        .eq("user_id", user.id)
        .eq("word", word.word)
        .maybeSingle();

      if (existing) {
        return { error: new Error("该单词已在生词本中") };
      }

      const { data, error } = await supabase
        .from("vocabulary")
        .insert({
          user_id: user.id,
          word: word.word,
          reading: word.reading || null,
          romaji: word.romaji || null,
          meaning: word.meaning || null,
          part_of_speech: word.part_of_speech || null,
          example_sentence: word.example_sentence || null,
          source_video_id: word.source_video_id || null,
          start_time: word.start_time || null,
          end_time: word.end_time || null,
        })
        .select()
        .single();

      if (error) throw error;

      setVocabulary((prev) => [data, ...prev]);
      return { data, error: null };
    } catch (error) {
      console.error("Error adding word:", error);
      return { error: error as Error };
    }
  };

  const deleteWord = async (wordId: string) => {
    if (!user) return { error: new Error("未登录") };

    try {
      const { error } = await supabase
        .from("vocabulary")
        .delete()
        .eq("id", wordId)
        .eq("user_id", user.id);

      if (error) throw error;

      setVocabulary((prev) => prev.filter((w) => w.id !== wordId));
      return { error: null };
    } catch (error) {
      console.error("Error deleting word:", error);
      return { error: error as Error };
    }
  };

  const updateWord = async (
    wordId: string,
    updates: Partial<Omit<VocabularyWord, "id" | "created_at">>
  ) => {
    if (!user) return { error: new Error("未登录") };

    try {
      const { data, error } = await supabase
        .from("vocabulary")
        .update(updates)
        .eq("id", wordId)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;

      setVocabulary((prev) =>
        prev.map((w) => (w.id === wordId ? data : w))
      );
      return { data, error: null };
    } catch (error) {
      console.error("Error updating word:", error);
      return { error: error as Error };
    }
  };

  useEffect(() => {
    fetchVocabulary();
  }, [user]);

  return {
    vocabulary,
    isLoading,
    addWord,
    deleteWord,
    updateWord,
    refetch: fetchVocabulary,
  };
}
